/**
 * @file medical-provider.schema.ts
 * @description Define o schema Zod para a entidade 'MedicalProvider'.
 *
 * Um MedicalProvider é uma **profissional** da rede — médica, enfermeira,
 * psicóloga, doula, obstetriz, fisioterapeuta —, podendo atender em múltiplos
 * locais.
 *
 * O modelo original previa também serviços de saúde (laboratório, clínica de
 * imagem). Em 10/09/2026 isso foi desfeito: um ano de operação mostrou que a
 * carteira real é só de profissionais, e a existência dos dois tipos custava
 * um seletor em toda tela e uma condicional em todo lugar que mostra ocupação.
 * Se um dia entrarem instituições, será por um caminho próprio — não por um
 * campo `type` nesta coleção.
 */

import { z } from 'zod';

import { LocationSchema } from './location.schema';

// --- Helper Schemas & Enums ---

/**
 * Define o tipo de provedor: um profissional individual ou um serviço.
 */
/**
 * `LABORATORY` e `IMAGING_CLINIC` estão **depreciados** e não são mais
 * oferecidos na interface.
 *
 * Continuam no enum de propósito: enum se alarga, nunca se restringe. Remover
 * os valores faria `findMany` lançar no primeiro documento antigo que os
 * tivesse — e derrubaria a listagem inteira de prestadores por causa de um
 * cadastro de 2025. Ver docs/migracoes/2026-09-rede-so-de-profissionais.md.
 */
const ProviderTypeEnum = z.enum([
  'INDIVIDUAL_PRACTITIONER',
  'LABORATORY',
  'IMAGING_CLINIC',
]);

/**
 * Define as ocupações possíveis para um profissional da saúde,
 * em linha com a identidade do produto (femtech).
 */
const OccupationEnum = z.enum([
  'MÉDICA',
  'ENFERMEIRA',
  'PSICÓLOGA',
  'DOULA',
  'OBSTETRIZ',
  'FISIOTERAPEUTA',
  'DENTISTA',
  'NUTRICIONISTA',
  /** Quem não cabe na lista — educadora perinatal, assistente social, consultora de amamentação… */
  'OUTRA',
]);

/**
 * Define os tipos de conselhos profissionais de saúde no Brasil.
 */
const ProfessionalIdTypeEnum = z.enum([
  'CRM', 'RQE', 'CRO', 'COREN', 'CRP', 'CRF', 'CREFITO', 'CRN', 'OTHER',
]);

/**
 * Define a estrutura para um único registro profissional,
 * combinando o tipo, o número e opcionalmente o estado.
 */
const ProfessionalIdSchema = z.object({
  type: ProfessionalIdTypeEnum,
  number: z.string(),
  state: z.string().length(2).nullable(),
});

/**
 * O conselho que cada ocupação costuma ter. `null` = a ocupação não tem
 * conselho (doula, e "outra" por definição) — para essas o registro é
 * opcional. É sugestão para a tela pré-preencher, não regra: uma obstetriz
 * pode ter COREN, uma médica pode preferir informar o RQE.
 */
const CONSELHO_POR_OCUPACAO: Record<z.infer<typeof OccupationEnum>, z.infer<typeof ProfessionalIdTypeEnum> | null> = {
  'MÉDICA': 'CRM',
  'ENFERMEIRA': 'COREN',
  'PSICÓLOGA': 'CRP',
  'DOULA': null,
  'OBSTETRIZ': 'COREN',
  'FISIOTERAPEUTA': 'CREFITO',
  'DENTISTA': 'CRO',
  'NUTRICIONISTA': 'CRN',
  'OUTRA': null,
};

/** A ocupação tem conselho? Se não, registro profissional é opcional. */
function ocupacaoTemConselho(occupation: string | null | undefined): boolean {
  if (!occupation) return true; // sem ocupação não dá para saber — a regra de sempre
  return CONSELHO_POR_OCUPACAO[occupation as z.infer<typeof OccupationEnum>] !== null;
}

/**
 * Prefixos que a tela oferece. O primeiro é o padrão: vazio, "sem
 * formalidades" — a paciente vê só o nome. O campo continua string livre no
 * documento; isto é a lista do select, não uma restrição.
 */
const PREFIXOS_SUGERIDOS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Sem formalidades' },
  { value: 'Dra.', label: 'Dra.' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Enf.', label: 'Enf.' },
  { value: 'Psic.', label: 'Psic.' },
  { value: 'Nutri.', label: 'Nutri.' },
  { value: 'Ft.', label: 'Ft.' },
  { value: 'Prof.', label: 'Prof.' },
  { value: 'Profa.', label: 'Profa.' },
];

/** Sugestão de "como quer ser chamada" a partir do nome completo: o primeiro nome. */
function sugestaoDeApelido(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] ?? '';
}

/**
 * Define a estrutura para o sumário dos feedbacks,
 * que é desnormalizado e atualizado via Cloud Functions.
 */
const FeedbackSummarySchema = z.object({
  averageRating: z.number().min(0).max(5),
  reviewCount: z.number().int().min(0),
});


// --- Main MedicalProvider Schema ---

/**
 * Define o schema principal e completo para a entidade MedicalProvider.
 */
const MedicalProviderSchema = z.object({
  // ID do provedor
  id: z.string(),

  // Prefixo opcional para o nome (ex: "Dra.", "Enf.").
  prefix: z.string().nullable(),

  // Nome de exibição do profissional ou do serviço.
  name: z.string(),

  // CPF do profissional (chave de busca para auto-criação)
  cpf: z.string().optional(),

  // URL para a foto de perfil ou logo do serviço.
  portraitUrl: z.string().url().or(z.literal('')).nullable(),

  // Descrição do profissional (formação, atuação), exibida no card de
  // agendamento. A copy específica de uma agenda pode sobrescrever isto via
  // `displayDescription` do CalendlyEventTypeMapping.
  bio: z.string().optional(),

  /**
   * Sempre `INDIVIDUAL_PRACTITIONER` no que se cria hoje. O default existe
   * para que quem grava não precise repetir o único valor válido, e para que
   * documento antigo sem o campo continue lendo.
   */
  type: ProviderTypeEnum.default('INDIVIDUAL_PRACTITIONER'),

  // Lista de locais/unidades onde o provedor oferece seus serviços.
  locations: z.array(LocationSchema),

  // Array de registros profissionais (CRM, RQE, etc.).
  professionalIds: z.array(ProfessionalIdSchema).optional(),

  // A ocupação da profissional.  
  occupation: OccupationEnum.optional(),

  // Lista de IDs que referenciam a coleção 'specialties'.
  specialtyIds: z.array(z.string()).optional(),

  // Objeto com os dados agregados dos feedbacks.
  feedbackSummary: FeedbackSummarySchema.nullable().default(null),

  // Afiliações de rede e convênios.
  isDefaultNetwork: z.boolean(),
  affiliatedPartnerIds: z.array(z.string()),

  // Campos de auditoria e exclusão lógica.
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().default(null),
}).refine((data) => data.isDefaultNetwork || data.affiliatedPartnerIds.length > 0, {
  message: "A profissional deve pertencer à Rede Padrão (Eva) ou estar vinculada a pelo menos uma marca.",
  path: ["affiliatedPartnerIds"], // Show error on this field
});


// --- Inferred TypeScript Types ---

type MedicalProvider = z.infer<typeof MedicalProviderSchema>;
type ProviderType = z.infer<typeof ProviderTypeEnum>;
type Occupation = z.infer<typeof OccupationEnum>;
type ProfessionalId = z.infer<typeof ProfessionalIdSchema>;
type ProfessionalIdType = z.infer<typeof ProfessionalIdTypeEnum>;
type FeedbackSummary = z.infer<typeof FeedbackSummarySchema>;


// --- Public Exports ---

export {
  MedicalProviderSchema,
  ProviderTypeEnum,
  OccupationEnum,
  ProfessionalIdSchema,
  ProfessionalIdTypeEnum,
  FeedbackSummarySchema,
  CONSELHO_POR_OCUPACAO,
  ocupacaoTemConselho,
  PREFIXOS_SUGERIDOS,
  sugestaoDeApelido,
};

export type {
  MedicalProvider,
  ProviderType,
  Occupation,
  ProfessionalId,
  ProfessionalIdType,
  FeedbackSummary,
};